import React, { useState, useEffect, useContext, useRef } from "react";
import { useLocation } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import Footer from "./Footer";
import { SettingsContext } from "../providers/SettingsProvider";
import { useJobDescriptions } from "../customHooks/useJobDescriptions";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";

import { getDbRef } from "../helperFunctions/determineType";
import MaterialsSearchBar from "./MaterialsSearchBar";

import "../css/materials.scss";
import {
  calcJobMaterials,
  calcJobsTotalCost,
  calcVat,
  // calcMarkupPercentage,
} from "../helperFunctions/calculationFunctions";
import IdentifierBar from "./IdentifierBar";
import { doc, onSnapshot, updateDoc } from "firebase/firestore";
import { SlidersContext } from "../providers/SlidersProvider";
import { tooltips } from "../popData";
import FooterBtn from "./FooterBtn";
// import MaterialsAddOn from "./MaterialsAddOn";

const Materials = () => {
  const location = useLocation();

  const { sliders, setSliderId } = useContext(SlidersContext);
  const { vat } = useContext(SettingsContext);
  const [showMats, setShowMats] = useState([]);

  const { currentMaterialsPercentage = 0 } = useContext(SettingsContext);

  const [materials, setMaterials] = useState(location.state.materials);
  // const [totalJobsCost] = useState(location.state.totalJobsCost);

  const [custom, setCustom] = useState({ name: "", price: 0 });

  const [importMessage, setImportMessage] = useState(null);
  const importMessageTimer = useRef(null);

  // console.log("Materials", materials);
  const [materialsCost, materialsBaseCost, materialsTotalMarkup] = calcJobMaterials({ materials });

  const [vatCost, inc] = calcVat(materialsCost, vat);

  // const markupPercentage = calcMarkupPercentage(
  //   materialsBaseCost,
  //   materialsCost
  // );

  let dbRef = getDbRef(location.state.type);

  // console.log("Location", location.state);
  const jobDescriptions = useJobDescriptions(location.state.timesheetId, dbRef);
  let totalJobsCost = calcJobsTotalCost(jobDescriptions, vat)[4];
  const [totalJobVatCost, incVatPrice] = calcVat(totalJobsCost, vat);

  const [jobDocRef] = useState(
    doc(dbRef, location.state.timesheetId, "jobDescriptions", location.state.jobDescId),
  );

  useEffect(() => {
    const unSubFromJobDescription = onSnapshot(jobDocRef, (snapshot) => {
      setMaterials(snapshot.data().materials);
    });

    return () => {
      // console.log("un subbing from singleJobDescription (materials)");
      unSubFromJobDescription();
    };
  }, [jobDocRef]);

  function shouldShow(id) {
    if (showMats.includes(id)) {
      return true;
    } else return false;
  }

  function toggleShow(id) {
    let newMats = [...showMats];

    if (shouldShow(id)) {
      newMats = newMats.filter((matId) => {
        if (id === matId) {
          return false;
        } else {
          return true;
        }
      });
    } else {
      newMats.push(id);
    }

    setShowMats(newMats);
  }

  function determineSign(value) {
    let sign = Math.sign(Number(value));
    if (sign < 0) {
      return "negative";
    } else if (sign > 0) {
      return "positive";
    } else {
      return "neutral";
    }
  }

  function calcNewAddOns(newPriceWithAddOns, { itemRefPrice }) {
    // Used when user changes the price of the material through the text input. Need to calculate new percentage add on
    // and price difference for the material.
    let typedNewPrice = Number(newPriceWithAddOns);
    //console.log("New price with add ons:", typedNewPrice, typeof typedNewPrice);

    let newPriceDifference = typedNewPrice - itemRefPrice;

    let newPercentage = ((typedNewPrice - itemRefPrice) / itemRefPrice) * 100;

    let validatedPercentage = Number.isNaN(newPercentage) ? 0 : newPercentage;

    return [Number(newPriceDifference.toFixed(2)), Number(validatedPercentage.toFixed(2))];
  }

  // handlePriceChange/handleInput below only update local state, on every keystroke/drag
  // tick - they used to also call updateDbMaterials() right there, which meant a single drag
  // of the markup-percentage slider (an <input type="range">, whose onChange fires
  // continuously while dragging, not just on release) could fire dozens of Firestore writes
  // in a couple of seconds, each one read straight back by this page's own onSnapshot
  // listener below. The actual Firestore write now only happens on commitMaterials, wired to
  // onBlur for the text/number inputs and onMouseUp/onTouchEnd for the slider - local state
  // stays instantly responsive either way, since that part is unchanged.
  const handlePriceChange = function (e) {
    let name = e.target.name;
    let value = e.target.value;
    let targetId = e.target.dataset.matid;

    let filteredMaterials = [...materials];
    // if (value === "0") {
    //   console.log("In filtered", value, typeof value);
    //   filteredMaterials = materials.filter((mat) => {
    //     return mat.materialCode !== targetId;
    //   });
    // }

    //console.log("filtered materials", filteredMaterials);

    let newMaterials = filteredMaterials.map((mat) => {
      if (mat.materialCode === targetId) {
        //calc new percentage and difference

        if (name === "priceWithAddOns") {
          let [priceDifference, addOnPercentage] = calcNewAddOns(value, mat);

          let newMat = {
            ...mat,
            [name]: value,
            priceDifference: priceDifference,
            addOnPercentage: addOnPercentage,
          };

          return newMat;
        } else if (name === "addOnPercentage") {
          let [priceDifference, priceWithAddOns] = calcPriceAddOns({
            itemRefPrice: mat.itemRefPrice,
            addOnPercentage: value,
          });

          return { ...mat, [name]: value, priceDifference, priceWithAddOns };
        } else if (name === "itemRefPrice") {
          let [priceDifference, priceWithAddOns] = calcPriceAddOns({
            itemRefPrice: value,
          });

          return { ...mat, [name]: value, priceDifference, priceWithAddOns };
        } else {
          return mat;
        }
      } else {
        return mat;
      }
    });

    setMaterials([...newMaterials]);
  };

  const handleInput = function (e) {
    let name = e.target.name;
    let value = e.target.value;
    let targetId = e.target.dataset.matid;

    //Remove materials whose quantity is 0
    let filteredMaterials = [...materials];
    if (name === "quantity" && value === "0") {
      filteredMaterials = materials.filter((mat) => {
        return mat.materialCode !== targetId;
      });
    }

    let newMaterials = filteredMaterials.map((mat) => {
      if (mat.materialCode === targetId) {
        return { ...mat, [name]: value };
      } else {
        return mat;
      }
    });

    setMaterials(newMaterials);
  };

  // The actual Firestore write - see the comment above handlePriceChange for why this is
  // separate from the local-state updates instead of firing on every keystroke/drag tick.
  function commitMaterials() {
    updateDbMaterials(materials);
  }

  function handleCustom(e) {
    let name = e.target.name;
    let value = e.target.value;
    setCustom({ ...custom, [name]: value });
  }

  function addVatToMaterial() {
    let priceWithVatAdded = (Number(custom.price) * 0.2 + Number(custom.price)).toFixed(2);
    setCustom({ ...custom, price: priceWithVatAdded });
  }

  function addCustom() {
    let [priceDifference, priceWithAddOns] = calcPriceAddOns({
      itemRefPrice: Number(custom.price),
    });

    let customMat = {
      materialDescription: custom.name,
      itemRefPrice: custom.price,
      category: "custom",
      priceDifference: Number(priceDifference),
      priceWithAddOns: Number(priceWithAddOns),
      addOnPercentage: currentMaterialsPercentage,
      quantity: 1,
      materialCode: uuidv4(),
    };

    let updatedMaterials = [...materials, customMat];
    updateDbMaterials(updatedMaterials);
  }

  function flashImportMessage(message) {
    setImportMessage(message);
    clearTimeout(importMessageTimer.current);
    importMessageTimer.current = setTimeout(() => setImportMessage(null), 3000);
  }

  // Imports a picks array exported by the external materials-picker tool, e.g.:
  //   [{ name, code, pick: { name, price, quantity } }, ...]
  // pick.name/price/quantity is the actual chosen product - the outer name/code are just the
  // search term and the tool's own internal id, not meaningful here. Merges onto the existing
  // materials list (matching addMaterial's own dedupe-by-description behaviour) rather than
  // replacing it, and writes once at the end rather than one Firestore update per item.
  async function importMaterials() {
    let text;
    try {
      text = await navigator.clipboard.readText();
    } catch (err) {
      flashImportMessage("Couldn't read the clipboard - check browser permissions and try again.");
      return;
    }

    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch (err) {
      flashImportMessage("Clipboard doesn't contain valid JSON.");
      return;
    }

    if (!Array.isArray(parsed) || parsed.length === 0) {
      flashImportMessage("Clipboard JSON isn't a non-empty array of materials.");
      return;
    }

    let updatedMaterials = [...materials];
    let importedCount = 0;

    for (let item of parsed) {
      let materialDescription = item?.pick?.name ?? item?.name;
      let itemRefPrice = Number(item?.pick?.price);
      let quantity = Number(item?.pick?.quantity) || 1;

      if (!materialDescription || Number.isNaN(itemRefPrice)) continue;

      let existingIndex = updatedMaterials.findIndex(
        (m) => m.materialDescription === materialDescription,
      );

      if (existingIndex !== -1) {
        updatedMaterials[existingIndex] = {
          ...updatedMaterials[existingIndex],
          quantity: Number(updatedMaterials[existingIndex].quantity) + quantity,
        };
      } else {
        let [priceDifference, priceWithAddOns] = calcPriceAddOns({ itemRefPrice });

        updatedMaterials.push({
          materialDescription,
          itemRefPrice,
          category: "custom",
          priceDifference: Number(priceDifference),
          priceWithAddOns: Number(priceWithAddOns),
          addOnPercentage: currentMaterialsPercentage,
          quantity,
          materialCode: uuidv4(),
        });
      }

      importedCount++;
    }

    if (importedCount === 0) {
      flashImportMessage("No valid materials found in the clipboard data.");
      return;
    }

    updateDbMaterials(updatedMaterials);
    flashImportMessage(`Imported ${importedCount} material${importedCount === 1 ? "" : "s"}.`);
  }

  function materialPresentCheck(m) {
    let isPresent = materials.find((mat) => {
      return mat.materialDescription === m.materialDescription;
    });

    return isPresent;
  }

  function updateDbMaterials(updatedMaterials) {
    updateDoc(jobDocRef, { materials: updatedMaterials });
  }

  function calcPriceAddOns({ itemRefPrice, addOnPercentage = currentMaterialsPercentage }) {
    // calculate new price with added percentage and the difference when the base price has either been
    // selected (adding new material) or changed (altered through changing text field.)
    let decimalPercentage = Number(addOnPercentage) / 100;

    let difference = Number(itemRefPrice) * Number(decimalPercentage);

    let priceWithAddOns = Number(itemRefPrice) * Number(decimalPercentage) + Number(itemRefPrice);

    return [difference.toFixed(2), priceWithAddOns.toFixed(2)];
  }

  function addMaterial(material) {
    let isPresent = materialPresentCheck(material);

    if (isPresent) {
      let newMaterials = materials.map((m) => {
        if (m.materialDescription === material.materialDescription) {
          return { ...m, quantity: Number(m.quantity) + 1 };
        } else {
          return m;
        }
      });

      let updatedMaterials = [...newMaterials];
      updateDbMaterials(updatedMaterials);
    } else {
      let [priceDifference, priceWithAddOns] = calcPriceAddOns(material);

      let addedMaterial = {
        ...material,
        quantity: 1,
        priceDifference: Number(priceDifference),
        priceWithAddOns: Number(priceWithAddOns),
        addOnPercentage: currentMaterialsPercentage,
        materialCode: uuidv4(),
      };

      let updatedMaterials = [...materials, addedMaterial];
      updateDbMaterials(updatedMaterials);
    }
  }

  function removeFromArray(matId) {
    return materials.filter((m) => {
      return m.materialCode !== matId;
    });
  }

  function removeMaterial(e) {
    let matId = e.currentTarget.name;
    let mats = removeFromArray(matId);

    updateDbMaterials(mats);
  }

  return (
    <div className="materialsPage">
      {vat ? (
        <div className="materialsPricesSummary">
          <p>
            <span>base:</span> <span>£{materialsBaseCost}</span>
          </p>
          <p>
            <span>markup:</span> <span>£{materialsTotalMarkup}</span>
          </p>
          <hr></hr>
          <p>
            <span>exc:</span> <span>£{materialsCost}</span>
          </p>
          <p>
            <span>vat:</span> <span>£{vatCost}</span>
          </p>
          <p>
            <span>inc:</span> <span>£{inc}</span>
          </p>
        </div>
      ) : (
        <div className="materialsPricesSummary">
          <p>
            <span>exc:</span> <span>£{materialsCost}</span>
          </p>
          <p>
            <span>markup:</span> <span>£{materialsTotalMarkup}</span>
          </p>
          <p>
            <span>inc:</span> <span>£{inc}</span>
          </p>
        </div>
      )}

      <Footer title={"Materials"} back={true}>
        {sliders && (
          <>
            <button
              title={tooltips.closeSlider}
              className="adder slider-control"
              onClick={() => setSliderId(null)}
            >
              <CloseIcon style={{ fontSize: 28 }} />
            </button>
            <hr />
          </>
        )}

        {vat && (
          <FooterBtn title={tooltips.vatMode} cb={null}>
            <span className="vat-button" style={{ color: `${vat ? "#24a218" : "#5d5d5d"}` }}>
              vat
            </span>
          </FooterBtn>
        )}
        <p className="adder secondary">£{Number(incVatPrice).toFixed(2)}</p>
        <p className="adder">£{inc}</p>
      </Footer>

      <IdentifierBar title="Materials" back={true} />
      {/* <hr /> */}
      <MaterialsSearchBar addMaterial={addMaterial} />

      <div className="addCustomLine">
        {/* <div className=""> */}
        <input
          className="customName"
          name={"name"}
          value={custom.name}
          onChange={handleCustom}
          type="text"
          placeholder="custom name"
        />
        {/* </div> */}
        {/* <div className=""> */}

        <div className="price-input">
          <span>£</span>
          <input
            className="arrowLess"
            step="any"
            name={"price"}
            value={custom.price}
            onChange={handleCustom}
            type="number"
            min="0"
            placeholder="0.00"
          />
        </div>

        {/* </div> */}
        <button className="customAdd" onClick={addCustom}>
          add
        </button>

        {/* <button className="customAdd addVat" onClick={addVatToMaterial}>
          vat ( £{(Number(custom.price) * 0.2 + Number(custom.price)).toFixed(2)} )
        </button> */}

        <div className="importControls">
          {importMessage && <span className="importMessage">{importMessage}</span>}
          <button
            className="importIconButton"
            onClick={importMaterials}
            title="Import from clipboard"
            aria-label="Import materials from clipboard"
          >
            <ContentPasteIcon />
          </button>
        </div>
      </div>

      <h3 className="">Materials</h3>

      {/* <MaterialsAddOn
        defaultMaterialsPercentage={currentMaterialsPercentage}
        materials={materials}
      /> */}

      <div className="materialsAcquiredList">
        {materials.map(
          ({
            materialDescription,
            itemRefPrice,
            priceWithAddOns,
            addOnPercentage,
            priceDifference,
            quantity,
            materialCode,
          }) => {
            // Need to distinguish between the new materials that have an added percentage
            // and the old materials that do not. The old timesheets still have to show up.

            // if (!addOnPercentage) {
            //   console.log("this is an old material");
            // }

            return (
              <div className="materialLineBox" key={materialCode}>
                <div className="materialLine">
                  <div className="formLine2">
                    {/* <label htmlFor="materialDescription">name</label> */}
                    <input
                      data-matid={materialCode}
                      // id={"materialDescription"}
                      name={"materialDescription"}
                      value={materialDescription}
                      onChange={handleInput}
                      onBlur={commitMaterials}
                      type="text"
                      autoComplete="off"
                      placeholder="material"
                    />
                  </div>

                  <div className="materialNumbersLine">
                    <div className="formLine2">
                      {/* <label htmlFor="itemRefPrice">price</label> */}
                      <div className="price-input">
                        <span>£</span>
                        <input
                          data-matid={materialCode}
                          className="arrowLess"
                          step="any"
                          placeholder="price"
                          // name={
                          //   addOnPercentage ? "priceWithAddOns" : "itemRefPrice"
                          // }
                          name={"priceWithAddOns"}
                          value={priceWithAddOns}
                          // id={addOnPercentage ? "priceWithAddOns" : "itemRefPrice"}
                          // value={addOnPercentage ? priceWithAddOns : itemRefPrice}
                          onChange={handlePriceChange}
                          onBlur={commitMaterials}
                          min="0"
                          type="number"
                        />
                      </div>
                    </div>

                    <div className="formLine2">
                      {/* <label htmlFor="materialDescription">quantity</label> */}
                      <input
                        data-matid={materialCode}
                        name={"quantity"}
                        className="mat-quantity"
                        // id={"quantity"}
                        value={quantity}
                        onChange={handleInput}
                        onBlur={commitMaterials}
                        type="number"
                        // placeholder="0"
                      />
                    </div>
                  </div>
                  <button
                    data-matid={materialCode}
                    className="mat-action"
                    // id={materialCode}
                    name={materialCode}
                    onClick={removeMaterial}
                  >
                    <DeleteIcon />
                  </button>
                  <button
                    className="show-percentages mat-action"
                    // name={materialCode}
                    onClick={(e) => {
                      toggleShow(materialCode);
                    }}
                  >
                    {shouldShow(materialCode) ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                  </button>
                </div>
                <div className={`material-percentages ${shouldShow(materialCode) ? "" : "closed"}`}>
                  <p>Base: £{Number(itemRefPrice).toFixed(2)}</p>
                  <p>
                    Markup: £{priceDifference}
                    {"   "}({" "}
                    <span className={determineSign(addOnPercentage ?? 0)}>{addOnPercentage ?? 0}%</span>{" "}
                    )
                  </p>

                  <input
                    // disabled={overrideActive}
                    id={"addOnPercentage"}
                    data-matid={materialCode}
                    name="addOnPercentage"
                    className="markup-slider"
                    type="range"
                    step="5"
                    min="0"
                    max="100"
                    value={addOnPercentage ?? 0}
                    onChange={handlePriceChange}
                    onMouseUp={commitMaterials}
                    onTouchEnd={commitMaterials}
                    onBlur={commitMaterials}
                  />
                </div>
              </div>
            );
          },
        )}
      </div>
    </div>
  );
};

export default Materials;
